import { memo, useState, useEffect } from 'react';

import { ImageIcon } from 'lucide-react';

import { useSetting } from '@/hooks/useSettings';
import { messagingApi } from '@/lib/api/messaging.api';
import { formatMessageTimestamp } from '@/lib/message-time';
import { cn } from '@/lib/utils';

import { MessageStatusIcon } from './MessageStatusIcon';

import type { MessagingMessage } from '@/lib/api/messaging.api';

const DEFAULT_STAFF_COLOR = '#047857';

const MediaImage = ({ mediaUrl }: { mediaUrl: string }) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    messagingApi
      .getMediaSignedUrl(mediaUrl)
      .then((url) => {
        if (!cancelled) setSignedUrl(url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mediaUrl]);

  if (loading) {
    return (
      <div className="flex h-32 w-48 items-center justify-center rounded-lg bg-black/10">
        <div className="border-primary h-5 w-5 animate-spin rounded-full border-b-2" />
      </div>
    );
  }

  if (error || !signedUrl) {
    return (
      <div className="flex h-32 w-48 items-center justify-center rounded-lg bg-black/10">
        <ImageIcon className="h-6 w-6 opacity-40" />
      </div>
    );
  }

  return (
    <img
      src={signedUrl}
      alt="Shared image"
      className="max-h-64 max-w-full rounded-lg object-contain"
      loading="lazy"
    />
  );
};

const QuotedMessage = ({
  content,
  direction,
  isParentOutbound,
}: {
  content: string;
  direction: 'INBOUND' | 'OUTBOUND' | null;
  isParentOutbound: boolean;
}) => (
  <div
    className={cn(
      'mb-1 rounded-lg border-l-4 px-2.5 py-1.5',
      isParentOutbound
        ? 'border-primary-foreground/40 bg-primary/80'
        : 'border-primary/40 bg-muted/80',
    )}
  >
    <p
      className={cn(
        'text-[10px] font-semibold',
        isParentOutbound ? 'text-primary-foreground/70' : 'text-primary',
      )}
    >
      {direction === 'OUTBOUND' ? 'You' : 'Customer'}
    </p>
    <p
      className={cn(
        'line-clamp-2 text-xs break-words',
        isParentOutbound ? 'text-primary-foreground/80' : 'text-foreground/70',
      )}
    >
      {content}
    </p>
  </div>
);

/** Memoized on the message reference — threads re-render far more often than a message changes. */
const MessageBubbleComponent = ({
  message,
  compact = false,
}: {
  message: MessagingMessage;
  /** Tighter spacing/typography for dense views like the multi-chat grid. */
  compact?: boolean;
}) => {
  const isOutbound = message.direction === 'OUTBOUND';
  const isImage = message.type === 'IMAGE' && !!message.media_url;
  const hasQuote = !!message.reply_to_content;
  // sender_id is only set when a team member sent the message; AI replies have none.
  const isStaff = isOutbound && !!message.sender_id;
  const { data: staffColorSetting } = useSetting('SALES_CONSOLE_STAFF_MESSAGE_COLOR');
  const staffColor = staffColorSetting?.value?.trim() || DEFAULT_STAFF_COLOR;

  return (
    <div className={cn('flex', isOutbound ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'rounded-2xl shadow-sm',
          compact ? 'max-w-[90%] px-2.5 py-1' : 'max-w-[min(80%,28rem)] px-3.5 py-2',
          isStaff
            ? 'rounded-br-md text-white'
            : isOutbound
              ? 'bg-primary text-primary-foreground rounded-br-md'
              : 'bg-muted rounded-bl-md',
        )}
        style={isStaff ? { backgroundColor: staffColor } : undefined}
        title={isStaff ? 'Sent by a team member' : undefined}
      >
        {hasQuote && (
          <QuotedMessage
            content={message.reply_to_content!}
            direction={message.reply_to_direction ?? null}
            isParentOutbound={isOutbound}
          />
        )}
        {isImage && <MediaImage mediaUrl={message.media_url!} />}
        {isImage && message.content && message.content !== '[Image]' && (
          <p
            className={cn('mt-1 break-words whitespace-pre-wrap', compact ? 'text-xs' : 'text-sm')}
          >
            {message.content}
          </p>
        )}
        {!isImage && (
          <p className={cn('break-words whitespace-pre-wrap', compact ? 'text-xs' : 'text-sm')}>
            {message.content ?? ''}
          </p>
        )}
        <div
          className={cn(
            'flex items-center gap-1',
            compact ? 'mt-0.5' : 'mt-1',
            isOutbound ? 'justify-end' : 'justify-start',
          )}
        >
          <span className={cn('opacity-60', compact ? 'text-[9px]' : 'text-[10px]')}>
            {formatMessageTimestamp(message.created_at)}
          </span>
          {isOutbound && <MessageStatusIcon status={message.status} />}
        </div>
        {message.status === 'FAILED' && message.error_message && (
          <p className="mt-1 text-xs text-red-300">{message.error_message}</p>
        )}
      </div>
    </div>
  );
};

export const MessageBubble = memo(MessageBubbleComponent);
